
import Component, { mixins } from 'vue-class-component';
import { Member } from '@/models/member';
import { memberRoute } from '@/router/coach';
import { Route } from 'vue-router';
import AssessBySkillMenu from '@/components/coach/assessmentBySkill/AssessBySkillMenu.vue';
import { SchemeNodeModel } from '@/models/schemeNodeModel';
import AdditionalAssessments from '@/components/coach/assessment/AdditionalAssessments.vue';
import { getForcePassed, getNodeIdsFromTree } from '@/services/utils';
import { GroupSchemeScope } from '@/components/mixins/schemescope';
import { GroupScope } from '@/components/mixins/groupscope';
import _intersection from 'lodash/intersection';
import _union from 'lodash/union';
import _difference from 'lodash/difference';

@Component({
  components: {
    AssessBySkillMenu,
    AdditionalAssessments,
  },
  name: 'AssessBySkill',
})
export default class AssessBySkill extends mixins(GroupSchemeScope, GroupScope) {
  created(): void {
    this.loadGroup();
  }

  /**
   * Constructs a router link path for the return to the current groups menu.
   *
   * @return {Route} - NOTE incomplete route returned.
   */
  get groupLink(): Route {
    return { path: memberRoute + '/' + this.$route.params.groupId } as Route;
  }

  /**
   * Computed getter.  Filters the schemes level only nodes by what levels are available for the members of the group.
   * There is no current link between level and groups so we need to narrow down to relevant nodes.
   */
  get groupsLevels(): Array<string> {
    const levelsForGroup = [] as Array<string>;
    // If group has levels and the course is not limit-level (from coursepro) then always show first level unless complete
    if (this.groupLevels !== null && !this.group.assessment_level_limit) {
      const firstLevel = this.getLevelsInOrder(this.groupLevels)[0];
      const completedNodes = this.groupMembers
        .filter(m => m.in)
        .map((member: Member) => Object.keys(member.assessments.complete || []));

      // Get any levels that are complete for everyone.
      const allCompleted = _intersection(...completedNodes);
      if (!allCompleted.includes(firstLevel)) {
        levelsForGroup.push();
      }
    }

    this.groupMembers
      .filter(m => m.in)
      .forEach((member: Member) => {
        const memberCurrentLevels = this.getMemberActiveLevels(member);
        const forcedNodes = getForcePassed(this.scheme, member);
        memberCurrentLevels.forEach((nodeId: string) => {
          if (
            !forcedNodes.includes(nodeId) &&
            levelsForGroup.indexOf(nodeId) < 0 &&
            (this.groupLevels === null || this.groupLevels.includes(nodeId))
          ) {
            levelsForGroup.push(nodeId);
          }
        });
      });
    return levelsForGroup;
  }

  get groupsLevelTree(): Array<SchemeNodeModel> {
    return this.schemeAllAssessableLevels(this.groupsLevels, false);
  }

  /**
   * Filters the schemes level only nodes by what levels are available for the members of the group.
   * There is no current link between level and groups so we need to narrow down to relevant nodes.
   */
  get completedLevels(): Array<SchemeNodeModel> {
    if (this.group.assessment_level_limit) {
      return [];
    }
    const completedNodes = this.groupMembers
      .filter(m => m.in)
      .map((member: Member) => Object.keys(member.assessments.complete || []));

    // Get any levels that are complete for everyone.
    const allCompleted = _intersection(...completedNodes);

    // Also show any levels that are complete by 1+ members but not in groupsLevelTree
    // For example one member has completed a level that the others do not have access to.
    const availableLevelIds = this.groupsLevelTree.flatMap(getNodeIdsFromTree);
    const someCompleted = _difference(_union(...completedNodes), availableLevelIds);

    return this.schemeAllCompleteLevels(_union(allCompleted, someCompleted), false);
  }
}
